import { useStoreSpecials } from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  SpecialCard,
  getDiscountLabel,
  getSpecialType,
} from '@iheartjane/menu';
import { Flex, TagFilledIcon, Typography } from '@iheartjane/reefer';
import type { SpecialType } from '@iheartjane/types';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useGetStoreSpecialsPage } from '@jane/shared-ecomm/data-access';
import type { Store } from '@jane/shared/types';

import { useComplianceLanguage, useMenu } from '../../../providers';
import type { MenuCategory } from '../../../types';
import {
  bySpecialType,
  capitalize,
  customCategoryLabel,
  isSupportedStoreSpecial,
  trackDealsCardTap,
} from '../../../util';
import { SpecialCard as OldSpecialCard } from '../SpecialCard/SpecialCard';
import { SpecialsGrid } from '../SpecialsGrid/SpecialsGrid';

const FEATURED_DISPLAY_LIMIT = 4;
const SPECIALS_DISPLAY_LIMIT = 6;

// Narrow shape the row card needs from a special. Both v1 (@jane/shared/types)
// and v3 (@iheartjane/data-access) StoreSpecial map into this — see mappers
// below — so the rendering branch is type-safe across both data sources.
interface RowSpecial {
  custom_badge: string | null;
  discount_amount: string | null;
  discount_type: string;
  id: number;
  photo: { urls: { original?: string | null } } | null;
  special_type: SpecialType;
  title: string;
}

interface StoreSpecialsRowProps {
  menuCategory?: MenuCategory;
  specialsProductCounts: { [specialId: number]: number };
  store: Store;
}

export const StoreSpecialsRow = ({
  menuCategory,
  specialsProductCounts,
  store,
}: StoreSpecialsRowProps) => {
  const specialsRedesignMenu = useFlag(FLAGS.c3SpecialsRedesignMenu);
  const extendedDealsLabelsEnabled = useFlag(FLAGS.c1ExtendedDealsLabels);
  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);
  const { getWord } = useComplianceLanguage();
  const navigate = useNavigate();
  const { visibleTabs } = useMenu();
  const { category = 'all' } = useParams<{
    category: MenuCategory;
  }>();

  const [isExpanded, setIsExpanded] = useState(false);

  const { specials_row_configs } = store;
  const isFeaturedPage = menuCategory === 'featured';
  const displayLimit = isFeaturedPage
    ? FEATURED_DISPLAY_LIMIT
    : SPECIALS_DISPLAY_LIMIT;
  const queryEnabled = specials_row_configs?.enabled ?? false;

  // Default perPage on both branches so the cache key matches MenuProvider's
  // fetch and React Query dedupes to a single request.
  const v1Query = useGetStoreSpecialsPage(store.id, {
    enabled: Boolean(!useV3 && queryEnabled),
  });

  const v3Query = useStoreSpecials({
    appMode: 'kiosk',
    enabled: Boolean(useV3 && queryEnabled),
    storeId: store.id,
  });

  const { isFetching, isLoading, fetchNextPage, hasNextPage } = useV3
    ? v3Query
    : v1Query;

  const v1Specials = useMemo<RowSpecial[]>(
    () =>
      (v1Query.data?.pages ?? [])
        .flatMap((page) => page.specials)
        .filter((s) => isSupportedStoreSpecial(s))
        .map((s) => ({
          custom_badge: s.custom_badge ?? null,
          discount_amount: s.discount_amount ?? null,
          discount_type: s.discount_type,
          id: s.id,
          photo: s.photo,
          special_type: s.special_type,
          title: s.title,
        })),
    [v1Query.data]
  );

  // v3 specials are server-filtered for enabled/promo_code/reservation_modes,
  // so isSupportedStoreSpecial is unneeded here.
  const v3Specials = useMemo<RowSpecial[]>(
    () =>
      (v3Query.data?.pages ?? [])
        .flatMap((page) => page.specials)
        .map((s) => ({
          custom_badge: s.custom_badge,
          discount_amount: s.discount_amount,
          discount_type: s.discount_type,
          id: s.id,
          photo: s.photo,
          special_type: s.special_type,
          title: s.title,
        })),
    [v3Query.data]
  );

  const allSpecials = useV3 ? v3Specials : v1Specials;
  const sortedSpecials = specialsRedesignMenu
    ? allSpecials
    : [...allSpecials].sort(bySpecialType);
  const supportedStoreSpecials = isExpanded
    ? sortedSpecials
    : sortedSpecials.slice(0, displayLimit);

  const showViewMore = isFeaturedPage
    ? true
    : !isFetching &&
      (isExpanded ? hasNextPage : sortedSpecials.length > displayLimit);
  const showViewLess =
    !isFeaturedPage && !isFetching && isExpanded && !hasNextPage;

  const navigateToSpecialsPage = () => {
    navigate(`/stores/${store.id}/menu/specials`, {
      state: {
        menuCategory,
      },
    });
  };

  const handleViewMore = () => {
    if (isFeaturedPage) {
      navigateToSpecialsPage();
    } else if (!isExpanded) {
      setIsExpanded(true);
    } else if (hasNextPage) {
      fetchNextPage();
    }
  };

  const handleViewLess = () => {
    setIsExpanded(false);
  };

  if (
    !isLoading &&
    (!specials_row_configs?.enabled || sortedSpecials.length === 0)
  ) {
    return null;
  }

  return (
    <SpecialsGrid
      onViewLessClick={isFeaturedPage ? undefined : handleViewLess}
      onViewMoreClick={handleViewMore}
      showViewLess={showViewLess}
      showViewMore={showViewMore}
      title={
        specialsRedesignMenu ? (
          <Flex flexDirection="row" gap={8} alignItems="center">
            <TagFilledIcon color="info" size="md" />
            <Typography variant="header-bold">
              {capitalize(getWord('deals') ?? 'Deals')}
            </Typography>
          </Flex>
        ) : (
          capitalize(getWord('deals') ?? 'Deals')
        )
      }
      isLoading={isLoading}
    >
      {supportedStoreSpecials?.map((special) => {
        const { custom_badge, id, photo, special_type, title } = special;
        const specialPhoto = photo ? photo.urls.original : null;
        const discountLabel = custom_badge
          ? custom_badge
          : getDiscountLabel({
              discountAmount: special.discount_amount,
              discountType: special.discount_type,
              specialType: special.special_type,
              storeComplianceLanguageSettings:
                store?.store_compliance_language_settings,
            });

        const onSpecialsClick = () => {
          const productKindLabel = customCategoryLabel({
            kind: category,
            store,
          });

          trackDealsCardTap({
            customImage: !!specialPhoto,
            dealName: [title],
            dealType: special_type,
            defaultTab: visibleTabs[0],
            menuName: category === 'specials' ? 'deals' : productKindLabel,
            menuOptionSelected:
              category === 'specials' ? 'deals tab' : productKindLabel,
            totalProductCount: Number(specialsProductCounts[id]),
          });
          navigate(`/stores/${store.id}/menu/special/${id}`, {
            state: {
              menuCategory,
            },
          });
        };

        return specialsRedesignMenu ? (
          <SpecialCard
            key={special.id}
            customSpecialBadge={special?.custom_badge}
            discountAmount={special?.discount_amount}
            discountType={special?.discount_type}
            extendedDealsLabelsEnabled={extendedDealsLabelsEnabled}
            imageSrc={special?.photo?.urls?.original}
            specialType={special?.special_type}
            storeComplianceLanguageSettings={
              store?.store_compliance_language_settings
            }
            title={special.title}
            onClick={onSpecialsClick}
          />
        ) : (
          //TODO: deprecate special view of this card when redesign is fully rolled out
          <OldSpecialCard
            key={id}
            discountLabel={discountLabel}
            onSpecialsClick={onSpecialsClick}
            specialPhoto={specialPhoto}
            storeSpecialType={getSpecialType(
              special_type,
              store?.store_compliance_language_settings,
              extendedDealsLabelsEnabled
            )}
            title={title}
            variant="store"
          />
        );
      })}
    </SpecialsGrid>
  );
};
